import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getBillCalendar,
  getDueBills,
} from "../../../services/panel/panelServices";

const WEEK_DAYS = ["S", "M", "T", "W", "T", "F", "S"];

const formatAmount = (amt) => `₹${Number(amt || 0).toLocaleString("en-IN")}`;

// "2026-11-04T00:00:00" → "2026-11-04" (no Date() to avoid timezone shifts)
const toDateKey = (value) => String(value || "").slice(0, 10);

const pad = (n) => String(n).padStart(2, "0");

const monthLabel = (year, month) =>
  new Date(year, month - 1, 1).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });

const dayLabel = (dateKey) => {
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
};

export default function DueDateCalendar() {
  const navigate = useNavigate();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); 

  const [isCalendarData, setCalendarData] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const [selectedDate, setSelectedDate] = useState("");
  const [isDueData, setDueData] = useState([]);
  const [dueLoading, setDueLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  // Ignore an older date's response if the user clicked another date meanwhile
  const latestDateRef = useRef("");

  useEffect(() => {
    fetchCalendar();
  }, [year, month]);

  const fetchCalendar = async () => {
    setCalendarLoading(true);
    setSelectedDate("");
    setDueData([]);
    setSelectedIds([]);
    try {
      const res = await getBillCalendar({ year, month });

      if (res?.statuscode === 1) {
        setCalendarData(res);
      } else {
        setCalendarData(null);
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch calendar",
        "error",
      );
    } finally {
      setCalendarLoading(false);
    }
  };

  const fetchDueBills = async (dateKey) => {
    latestDateRef.current = dateKey;
    setDueLoading(true);
    setDueData([]);
    setSelectedIds([]);
    try {
      const res = await getDueBills({ date: dateKey });

      if (latestDateRef.current !== dateKey) return;

      if (res?.statuscode === 1) {
        const records = res?.records || [];
        setDueData(records);
        // Locked bills are already in an open batch — tick only the free ones
        setSelectedIds(
          records.filter((b) => !b.isLocked).map((b) => b.consumerAccountID),
        );
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch due bills",
        "error",
      );
    } finally {
      if (latestDateRef.current === dateKey) setDueLoading(false);
    }
  };

  const changeMonth = (step) => {
    const d = new Date(year, month - 1 + step, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const handleSelectDate = (dateKey) => {
    setSelectedDate(dateKey);
    fetchDueBills(dateKey);
  };

  const toggleBill = (id) =>
    setSelectedIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );

  const eligibleIds = isDueData
    .filter((b) => !b.isLocked)
    .map((b) => b.consumerAccountID);

  const handleSelectAll = () => setSelectedIds(eligibleIds);

  const handleSendToReview = () =>
    navigate("/bill-review", { state: { consumerAccountIDs: selectedIds } });

  // Calendar cells: blanks before the 1st, then one cell per day
  const dueByDate = Object.fromEntries(
    (isCalendarData?.days || []).map((d) => [toDateKey(d.date), d]),
  );
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const selectedTotal = isDueData
    .filter((b) => selectedIds.includes(b.consumerAccountID))
    .reduce((sum, b) => sum + Number(b.amount || 0), 0);

  return (
    <>
      <div className="screen active" id="screen-calendar">
        <div className="page-head">
          <div>
            <h1>Due-date calendar</h1>
            <div className="ph-sub">
              {monthLabel(year, month)} ·{" "}
              {isCalendarData?.totalCount
                ? `${isCalendarData.totalCount} bills due · ${formatAmount(
                    isCalendarData.totalAmount,
                  )}`
                : "no bills due"}{" "}
              · click a highlighted date to see what's due
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => changeMonth(-1)}
              disabled={calendarLoading}
            >
              ‹ Prev
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => changeMonth(1)}
              disabled={calendarLoading}
            >
              Next ›
            </button>
          </div>
        </div>
        <div className="grid-2">
          <div className="card">
            <div className="card-title" style={{ marginBottom: 12 }}>
              {monthLabel(year, month)}
            </div>
            <div className="cal-grid">
              {WEEK_DAYS.map((d, i) => (
                <div key={i} className="cal-dow">
                  {d}
                </div>
              ))}
            </div>
            <div
              className="cal-grid"
              style={{ marginTop: 6, opacity: calendarLoading ? 0.5 : 1 }}
            >
              {Array.from({ length: firstWeekday }, (_, i) => (
                <div key={`blank-${i}`} className="cal-cell empty" />
              ))}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const dateKey = `${year}-${pad(month)}-${pad(day)}`;
                const due = dueByDate[dateKey];
                return (
                  <div
                    key={dateKey}
                    className={`cal-cell ${due ? "due" : ""} ${
                      selectedDate === dateKey ? "selected" : ""
                    }`}
                    // Only dates with bills are clickable
                    style={due ? undefined : { cursor: "default" }}
                    title={
                      due
                        ? `${due.count} bill(s) · ${formatAmount(due.total)}`
                        : undefined
                    }
                    onClick={() => due && handleSelectDate(dateKey)}
                  >
                    {day}
                    {due && <div className="cc-count">{due.count}</div>}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="card">
            <div className="card-title">
              {selectedDate
                ? `${isDueData.length} bills due on ${dayLabel(selectedDate)}`
                : "Select a date"}
            </div>
            <div className="card-sub">
              {selectedDate
                ? `${selectedIds.length} selected · ${formatAmount(
                    selectedTotal,
                  )}`
                : "Bills due on that date will appear here"}
            </div>

            {dueLoading ? (
              <div className="empty-note">Loading bills...</div>
            ) : selectedDate && isDueData.length === 0 ? (
              <div className="empty-note">No bills due on this date</div>
            ) : (
              isDueData.map((bill) => (
                <label
                  key={bill.consumerAccountID}
                  className="due-row checkbox-row"
                  style={{
                    cursor: bill.isLocked ? "not-allowed" : "pointer",
                    opacity: bill.isLocked ? 0.55 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(bill.consumerAccountID)}
                    disabled={bill.isLocked}
                    onChange={() => toggleBill(bill.consumerAccountID)}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>
                      {bill.billerName}
                      {bill.nickname && ` — ${bill.nickname}`}
                    </div>
                    <div
                      style={{ fontSize: 10.5, color: "var(--text-faint)" }}
                      className="mono"
                    >
                      {bill.accountNumber} · {bill.categoryName}
                    </div>
                    {bill.isLocked ? (
                      <span className="pill pill-neutral" style={{ marginTop: 4 }}>
                        In {bill.lockedBatchNo}
                      </span>
                    ) : (
                      !bill.fetchedToday && (
                        <div style={{ fontSize: 10.5, color: "var(--amber)" }}>
                          Fetched earlier — amount may change at payment
                        </div>
                      )
                    )}
                  </div>
                  <div className="mono" style={{ fontWeight: 600 }}>
                    {formatAmount(bill.amount)}
                  </div>
                </label>
              ))
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleSelectAll}
                disabled={!eligibleIds.length}
              >
                Select all eligible
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSendToReview}
                disabled={!selectedIds.length}
              >
                Send to bill review
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
